import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';
import styled from 'styled-components';

import { GroupsIcon } from '@treams/treams-icons';
import { Avatar } from '@treams/treams-ui/common/Avatar';
import {
	DateIntervalNavigation,
	ITimeMachine,
} from '@treams/treams-ui/common/DateIntervalNavigation';
import { Sidebar } from '@treams/treams-ui/common/Sidebar';
import { Heading3, Paragraph } from '@treams/treams-ui/common/Text';
import { tokens } from '@treams/treams-ui/theme/tokens';

const SidebarContainer = styled(Box)`
	background: ${tokens.surface.secondary};
	height: 100%;

	@media print {
		display: none;
	}
`;

export type GrowthPathOverviewGiver = {
	id: string;
	firstName: string;
	lastName: string;
	email?: string;
};

type Props = {
	givers: GrowthPathOverviewGiver[];
	selectedGiverId: string | null;
	onGiverChange: (giverId: string | null) => void;
	timeMachine: ITimeMachine;
	onTimeMachineChange: (timeMachine: ITimeMachine) => void;
};

const GrowthPathOverviewSidebar: FC<Props> = ({
	givers,
	selectedGiverId,
	onGiverChange,
	timeMachine,
	onTimeMachineChange,
}) => {
	const { t } = useTranslation();

	return (
		<SidebarContainer p={3}>
			<Paragraph>{t('competency.sidebar.dateFilter.description')}</Paragraph>

			<Box mt={4}>
				<Heading3>{t('competency.sidebar.dateFilter.title')}</Heading3>
				<Box mt={2}>
					<DateIntervalNavigation value={timeMachine} onChange={onTimeMachineChange} disableQuery />
				</Box>
			</Box>

			<Box mt={4}>
				<Heading3>{t('competency.sidebar.givers.title')}</Heading3>
				<Paragraph>{t('competency.sidebar.givers.description')}</Paragraph>
				<Box mt={2}>
					<Sidebar.Item
						small
						icon={<GroupsIcon />}
						title={t('competency.sidebar.givers.all')}
						selected={selectedGiverId === null}
						aria-pressed={selectedGiverId === null}
						onClick={() => onGiverChange(null)}
					/>
				</Box>
				{givers.map((giver) => (
					<Box key={giver.id} mt={2}>
						<Sidebar.Item
							small
							title={`${giver.firstName} ${giver.lastName}`}
							subtitle={giver.email}
							icon={<Avatar user={giver} size={32} flexGrow={0} flexShrink={0} />}
							selected={selectedGiverId === giver.id}
							aria-pressed={selectedGiverId === giver.id}
							onClick={() => onGiverChange(giver.id)}
						/>
					</Box>
				))}
			</Box>
		</SidebarContainer>
	);
};

export default GrowthPathOverviewSidebar;
